/*
 * THE SERVER A RECIPIENT'S VIEWER TALKS TO (docs/SHARING_SECURITY.md). A recipient has no account, and the dashboard that draws the clip asks "its server" for the site, its pages, the day's numbers, the account. In a
 * shared clip's page ALL of those are answered HERE, from the clip, inside the page: the HTTP layer's adapter is replaced (util/http.ts), so no request the dashboard makes ever leaves the browser. That is a
 * security property, not a convenience: a viewer that could call the real API would call it with whatever cookies the recipient has (they may be a Skydive customer in this very browser) and a clip's data must
 * never be able to cause that.
 *
 * The dashboard's own calls were MEASURED (a recipient's viewer, 13 kinds of call); each is answered from the clip or with an empty answer of the right shape. Anything it asks that is not known gets an empty answer
 * too, never a pass-through. The page boxes belong to the recipient while they watch (a small in-memory store, the demo's own); the clip's replacement answers are src/share/shareServer.ts's.
 */
import { PAGE_SLOT_COUNT } from "../util/slotCount";
import { createPageStore } from "../demo/demoPages";
import { getActiveShare, registerPageSeeder, shareAnswer } from "./shareServer";
import { boxesLocked } from "./shareLock";

export const VIEWER_SITE_ID = "shared-clip";
export const VIEWER_USER_ID = "shared-clip-viewer";

/** the stand-in the dashboard is "logged in" as: no real account, no real id, no permissions beyond looking */
export const viewerUser = {
  _id: VIEWER_USER_ID, email: "", first_name: "Viewer", last_name: "", name: "Viewer",
  flags: { activated: true, trialFinished: false, tourFinished: true, ghostOnboardingSeen: true, emailVerified: true },
  settings: {} as Record<string, unknown>, sitesToTrack: 1, role: "user",
  site: { _id: VIEWER_SITE_ID, host: "", name: "", userId: VIEWER_USER_ID, tracking: true, connected: true, pages: [] as unknown[] },
};

const pageStore = createPageStore([]);
registerPageSeeder((pages) => { pageStore.replaceAll(pages as any); });

const siteNow = () => ({ ...viewerUser.site, name: getActiveShare()?.siteName || "", pages: pageStore.pages() });
// every page box there is (PAGE_SLOT_COUNT), as every plan above the free one has: the owner's boxes are all there
const account = {
  _id: "shared-clip-account", userId: VIEWER_USER_ID, flags: { activated: true, trialFinished: false }, changeDate: new Date(0).toISOString(),
  subscriptionData: { title: "Shared clip", price: 0, billingCycle: 30, mainViewSlots: PAGE_SLOT_COUNT, pageViewsLimit: 1_000_000, pageViewsLimitType: "month", restrictedRealtimeFilters: [], restrictedRangeFilters: [], restrictedPagination: false, storageDays: -1, sitesToTrack: 1, urlsToTrack: -1 },
};
const zeroMetrics = { totalSession: 0, newUsers: 0, bounceRate: 0, avgSessionDuration: 0, pageviewsPerSession: 0, pageViews: 0, conversions: 0, conversionRate: 0 };

type Handler = (params: any) => unknown;
const handlers: Record<string, Handler> = {
  getSite: siteNow, getUsersSite: siteNow, getUsersSites: () => [siteNow()],
  getAccountRecord: () => account,
  // with the boxes locked (the owner's choice) nothing here changes them, whatever the page asks: the answer is the pages as they are
  selectPage: (p) => (boxesLocked() ? { pages: pageStore.pages() } : pageStore.select(p.pageName, p.slotIndex)),
  unselectPage: (p) => (boxesLocked() ? { pages: pageStore.pages() } : pageStore.unselect(p.pageName)),
  reorderPages: (p) => (boxesLocked() ? { pages: pageStore.pages() } : pageStore.reorder(p.moves)),
  updatePagesSorting: () => ({ pages: pageStore.pages() }),
  getMainMetrics: () => zeroMetrics,
  getPageVisitsCount: () => [],
  getPaymentMethod: () => ({}),
  getPageViewsRenewalDate: () => null,
  getNotes: () => ({ ok: true, notes: [] }),
  getGoals: () => [],
  // "save what this person is looking at" is the owner's own setting: nothing is saved in a clip's page
  update: () => null,
};

/** the dashboard's answer to one call, from the clip (or an empty one). Exported so a test can ask it without a browser. */
export const answerViewerCall = (method: string, params: unknown): unknown => {
  const known = Object.prototype.hasOwnProperty.call(handlers, method) ? handlers[method] : undefined;
  const base = known ? known(params || {}) : null;
  const shared = shareAnswer(method, base);

  return shared !== undefined ? shared : base;
};

const copy = <T,>(value: T): T => (value === undefined ? value : (JSON.parse(JSON.stringify(value)) as T));

/** replaces axios's network adapter: EVERY request is answered in the page, and nothing is ever sent */
export const installViewerServer = (http: any) => {
  http.defaults.adapter = async (config: any) => {
    const url = String(config.url || "");
    let body: any = {};
    try { body = typeof config.data === "string" ? JSON.parse(config.data) : config.data || {}; } catch { body = {}; }
    let data: any;
    if (/user\/currentUser$/.test(url)) data = copy(viewerUser);
    else if (body && typeof body.method === "string") data = { jsonrpc: "2.0", id: body.id ?? 0, result: copy(answerViewerCall(body.method, body.params)) };
    else data = {};

    return { data, status: 200, statusText: "OK", headers: {}, config, request: {} };
  };
};
